import { useCallback, useEffect, useState } from "react";
import { notification } from "antd";

import { TestInstructionsHttpService } from "@/shared/services/tests/test-instructions-http.service";
import { getFriendlyErrorMessage } from "@/shared/services/http";

export function useGetTestInstructions(testId: number) {
  const [testInstructions, setTestInstructions] = useState<Record<string, string> | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchTestInstructions = useCallback(async () => {
    try {
      setIsLoading(true);
      setTestInstructions(await TestInstructionsHttpService.getTestInstructionsByTestId(testId));
    } catch (error) {
      notification.error({
        message: getFriendlyErrorMessage(error) ?? "Failed to load test instructions. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  }, [testId]);

  useEffect(() => {
    fetchTestInstructions();
  }, [fetchTestInstructions]);

  // The update endpoint returns no body and stores each locale's body verbatim, so after a save we
  // merge what we just persisted into local state rather than refetching — keeps the form (and the
  // "already has a row" set used by the save filter) in sync with the DB without a round-trip.
  const applySavedInstructions = useCallback((saved: Record<string, string>) => {
    setTestInstructions((current) => ({ ...current, ...saved }));
  }, []);

  return { testInstructions, isLoading, applySavedInstructions };
}
